import { useEffect, useRef, useState } from "react";
import { Check, ChevronsUpDown, MapPin, Plane, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

export const LocationPicker = ({ locations, value, onChange, placeholder, testId, remote = false, searchPlaceholder }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedObj, setSelectedObj] = useState(null);
  const debounceRef = useRef(null);

  // Keep a display label for the current value even when it's a live-searched
  // hotel that isn't part of the preloaded list.
  useEffect(() => {
    const found = locations.find((l) => l.id === value) || (selectedObj?.id === value ? selectedObj : null);
    setSelectedObj(found || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, locations]);

  useEffect(() => {
    if (!remote) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/locations", { params: { q, limit: 25 }, timeout: 12000 });
        setResults((data.locations || []).filter((l) => l.type !== "airport"));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 280);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, remote]);

  const list = remote ? (query.trim().length >= 2 ? results : locations) : locations;
  const selected = selectedObj || locations.find((l) => l.id === value);

  const pick = (l) => {
    setSelectedObj(l);
    onChange(l.id, l);
    setOpen(false);
    setQuery("");
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <PopoverTrigger asChild>
        <button
          data-testid={testId}
          type="button"
          className="flex h-11 w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 text-left text-sm transition-colors duration-200 hover:border-sea focus:outline-none focus:ring-2 focus:ring-sea/40"
        >
          <span className="flex min-w-0 items-center gap-2">
            {selected?.type === "airport" ? (
              <Plane size={15} className="shrink-0 text-sea" />
            ) : (
              <MapPin size={15} className="shrink-0 text-slate-400" />
            )}
            <span className={cn("truncate", !selected && "text-slate-400")}>
              {selected ? selected.name : placeholder}
            </span>
          </span>
          <ChevronsUpDown size={14} className="ml-2 shrink-0 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[300px] bg-white p-0" align="start">
        <Command shouldFilter={!remote}>
          <CommandInput
            placeholder={searchPlaceholder || placeholder}
            data-testid={`${testId}-search`}
            value={remote ? query : undefined}
            onValueChange={remote ? setQuery : undefined}
          />
          <CommandList className="max-h-80">
            {searching ? (
              <div className="flex items-center gap-2 px-3 py-6 text-sm text-slate-400">
                <Loader2 size={15} className="animate-spin" /> …
              </div>
            ) : (
              <CommandEmpty>{remote && query.trim().length < 2 ? "" : "—"}</CommandEmpty>
            )}
            <CommandGroup>
              {list.map((l) => (
                <CommandItem
                  key={l.id}
                  value={`${l.name} ${l.area || ""} ${l.id}`}
                  data-testid={`${testId}-option-${l.id}`}
                  onSelect={() => pick(l)}
                >
                  {l.type === "airport" ? (
                    <Plane size={14} className="mr-2 shrink-0 text-sea" />
                  ) : (
                    <MapPin size={14} className="mr-2 shrink-0 text-slate-400" />
                  )}
                  <span className="flex-1 truncate">{l.name}</span>
                  {l.area && l.area !== l.name && <span className="ml-2 shrink-0 text-xs text-slate-400">· {l.area}</span>}
                  {value === l.id && <Check size={14} className="ml-2 shrink-0 text-sea" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
